import { apiClient } from '@/lib/api';
import { ENDPOINTS } from '@/lib/api/endpoints';
import type { Message } from '../components/chat-message';
import type { ToolResultDisplay } from '../components/tool-result-renderer';

interface ChatRequest {
  message: string;
  history: Array<{ role: string; content: string }>;
  pageContext?: string;
  conversationId?: string;
}

interface ChatResponse {
  response: string;
  toolResults?: ToolResultDisplay[];
  conversationId: string;
}

export interface ConversationSummary {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  _count: { messages: number };
}

export interface ConversationDetail {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messages: Array<{
    id: string;
    role: 'user' | 'assistant';
    content: string;
    pageContext: any;
    createdAt: string;
  }>;
}

class ChatAssistantService {
  async sendMessage(
    message: string,
    history: Message[],
    pageContext?: string,
    conversationId?: string
  ): Promise<{ response: string; toolResults: ToolResultDisplay[]; conversationId: string }> {
    const chatHistory = history.map((msg) => ({
      role: msg.role,
      content: msg.content,
    }));

    const payload: ChatRequest = {
      message,
      history: chatHistory,
    };

    if (pageContext) payload.pageContext = pageContext;
    if (conversationId) payload.conversationId = conversationId;

    const data = await apiClient.post<ChatResponse>(ENDPOINTS.AI_CHAT, payload);

    return {
      response: data.response,
      toolResults: data.toolResults || [],
      conversationId: data.conversationId,
    };
  }

  async getConversations(): Promise<ConversationSummary[]> {
    return apiClient.get<ConversationSummary[]>(ENDPOINTS.AI_CONVERSATIONS);
  }

  async getConversation(id: string): Promise<ConversationDetail> {
    return apiClient.get<ConversationDetail>(ENDPOINTS.AI_CONVERSATION_BY_ID(id));
  }

  async deleteConversation(id: string): Promise<void> {
    await apiClient.delete(ENDPOINTS.AI_CONVERSATION_BY_ID(id));
  }
}

export const chatAssistantService = new ChatAssistantService();
export default chatAssistantService;
